import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, Loader2, Inbox, Clock, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A short curated list of common timezones (works alongside any tz the user already has on file)
const TIMEZONES = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Hong_Kong',
  'Australia/Sydney',
  'UTC',
];

const HOURS = Array.from({ length: 24 }, (_, h) => h);

function formatHour(h) {
  const ampm = h < 12 ? 'AM' : 'PM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${ampm}`;
}

function tzShort(tz) {
  return tz.replace(/_/g, ' ').split('/').pop();
}

export default function LeadDigestSettingsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [hour, setHour] = useState(8);
  const [tz, setTz] = useState('America/Los_Angeles');
  const [lastSentAt, setLastSentAt] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/lead-digest`, { headers });
        setEnabled(Boolean(data.enabled));
        setHour(typeof data.hour === 'number' ? data.hour : 8);
        setTz(data.timezone || 'America/Los_Angeles');
        setLastSentAt(data.last_lead_digest_at || null);
      } catch (e) {
        toast.error('Failed to load digest settings');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const tzOptions = TIMEZONES.includes(tz) ? TIMEZONES : [tz, ...TIMEZONES];

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/settings/lead-digest`,
        { enabled, hour, timezone: tz },
        { headers }
      );
      setDirty(false);
      toast.success('Digest settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto" data-testid="lead-digest-settings-page">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      <header className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Inbox size={20} />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Lead Digest Settings</h1>
          <p className="text-xs text-muted-foreground">Control your daily lead summary email</p>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20" data-testid="settings-loading">
          <Loader2 className="animate-spin text-muted-foreground" size={28} />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Daily Lead Digest</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Get a daily summary of new leads and urgent calls.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enabled toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Enable Daily Digest</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sends one email per day with lead counts and your top leads.
                </p>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={(v) => {
                  setEnabled(v);
                  setDirty(true);
                }}
                data-testid="digest-enabled-toggle"
              />
            </div>

            {/* Hour picker */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <Clock size={14} /> Send at
              </Label>
              <Select
                value={String(hour)}
                onValueChange={(v) => {
                  setHour(parseInt(v, 10));
                  setDirty(true);
                }}
                disabled={!enabled}
              >
                <SelectTrigger className="mt-1.5" data-testid="digest-hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map((h) => (
                    <SelectItem key={h} value={String(h)} data-testid={`digest-hour-option-${h}`}>
                      {formatHour(h)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Timezone picker */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <Globe size={14} /> Timezone
              </Label>
              <Select
                value={tz}
                onValueChange={(v) => {
                  setTz(v);
                  setDirty(true);
                }}
                disabled={!enabled}
              >
                <SelectTrigger className="mt-1.5" data-testid="digest-timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {tzOptions.map((zone) => (
                    <SelectItem key={zone} value={zone} data-testid={`digest-tz-option-${zone}`}>
                      {tzShort(zone)} <span className="text-muted-foreground text-xs ml-1">({zone})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview text */}
            <div className="rounded-lg bg-muted/50 border border-border p-3" data-testid="digest-preview">
              <p className="text-xs text-muted-foreground">
                {enabled ? (
                  <>
                    You’ll receive your digest at{' '}
                    <span className="text-foreground font-medium">{formatHour(hour)}</span> (
                    <span className="text-foreground">{tzShort(tz)}</span>).
                  </>
                ) : (
                  <>Daily digest is currently <span className="text-foreground font-medium">disabled</span>.</>
                )}
              </p>
              {lastSentAt && (
                <p className="text-xs text-muted-foreground mt-1">
                  Last sent: <span className="text-foreground">{new Date(lastSentAt).toLocaleString()}</span>
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button
                onClick={save}
                disabled={saving || !dirty}
                data-testid="save-digest-btn"
              >
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save size={14} /> Save changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
